<template>
  <component
    :is="wrapperComponent"
    :placement="placement"
    :mount-to-body="mountToBody"
    default-slot-classes="flex items-center h-full"
    :class="$attrs.class"
    :include-cta="isDeprecatedMop"
    :button-text="ctaText"
    :cta-icon="ctaIcon"
    :stop-propagation="isDeprecatedMop"
    @cta-clicked="handleCtaClicked"
  >
    <BittsTag v-if="isTag" :color="tagColor" :size="size">
      <div class="flex items-center h-full">
        <FontAwesomeIcon
          v-if="showIcon"
          :icon="['fak', icon]"
          :style="iconStyle"
          class="mr-4"
          :class="iconClass"
        />
        <span :class="labelClass">
          {{ label }}
        </span>
      </div>
    </BittsTag>
    <FontAwesomeIcon
      v-else-if="showIcon"
      :icon="['fak', icon]"
      :style="iconStyle"
      :class="iconClass"
    />
    <template #title>
      <template v-if="isDeprecatedMop">
        Managed Offline Partners are no longer maintained.
        <br /><br />
        <template v-if="hasExistingOpenDataPartner">
          {{ partner.name }} is also an Open Data Partner. Delete this older
          record to clean up your list, or keep it to preserve its historical
          reports and lists.
        </template>
        <template v-else>
          Replace this partner with an Open Data Partner to continue receiving
          maintained public insights.
        </template>
      </template>
      <template v-else>
        {{ isTag ? tooltipLabel : label }}
      </template>
    </template>
  </component>
  <Teleport to="body">
    <ReplaceOpenDataPartnerDialog
      v-if="isReplaceDialogVisible"
      v-model="isReplaceDialogVisible"
      :preselected-partner-org-id="partner.id"
    />
    <DeletePartnershipModal
      v-if="isDeleteDialogVisible"
      :partner-org-id="partner.id"
      @closed="isDeleteDialogVisible = false"
    />
  </Teleport>
</template>

<script setup lang="ts">
import type { BittsIcon, BittsSize, PlacementType } from '@crossbeam/bitts';
import { BittsTag, BittsTooltip } from '@crossbeam/bitts';
import type { StrictExclude } from '@crossbeam/types';

import { computed, ref } from 'vue';

import DeletePartnershipModal from '@/components/partners/DeletePartnershipModal.vue';
import ReplaceOpenDataPartnerDialog from '@/components/partners/ReplaceOpenDataPartnerDialog.vue';

import useDeprecatedManagedOfflinePartner from '@/composables/useDeprecatedManagedOfflinePartner';
import useHasOpenDataPartnerAccess from '@/composables/useHasOpenDataPartnerAccess';
import type { Partner, PartnerTrimmed } from '@/types/partners';

const {
  partner,
  isTag = true,
  placement = 'topLeft',
  showIcon = true,
  size = 'x-small',
  mountToBody = false,
  showTooltip = true,
  hasDeprecatedMopActions = true,
} = defineProps<{
  partner: Partner | PartnerTrimmed;
  isTag?: boolean;
  placement?: PlacementType;
  showIcon?: boolean;
  showTooltip?: boolean;
  size?: StrictExclude<BittsSize, 'tiny' | 'medium' | 'large' | 'x-large'>;
  mountToBody?: boolean;
  hasDeprecatedMopActions?: boolean;
}>();

const emit = defineEmits<{
  'cta-clicked': [];
}>();

const hasAddOpenDataPartner = useHasOpenDataPartnerAccess();
const { isDeprecatedMop, hasExistingOpenDataPartner } =
  useDeprecatedManagedOfflinePartner(() => partner);

defineOptions({
  inheritAttrs: false,
});

const iconStyle = { height: '16px', width: '16px', color: 'currentColor' };

const isReplaceDialogVisible = ref(false);
const isDeleteDialogVisible = ref(false);

function handleCtaClicked() {
  if (!hasDeprecatedMopActions) {
    emit('cta-clicked');
    return;
  }
  if (hasExistingOpenDataPartner.value) {
    isDeleteDialogVisible.value = true;
    return;
  }
  isReplaceDialogVisible.value = true;
}

const ctaText = computed(() => {
  if (!isDeprecatedMop.value) return '';
  if (!hasDeprecatedMopActions) return 'View Partner';
  return hasExistingOpenDataPartner.value
    ? 'Delete Partner'
    : 'Replace Partner';
});

const ctaIcon = computed<BittsIcon | null>(() => {
  if (!isDeprecatedMop.value) return null;
  if (!hasDeprecatedMopActions) return ['far', 'arrow-right'];
  return hasExistingOpenDataPartner.value
    ? ['fak', 'delete']
    : ['far', 'shuffle'];
});

const isManagedPartner = computed(() => partner?.virtual_partner);

const isOfflinePartner = computed(() => partner?.offline_partner);

const wrapperComponent = computed(() => {
  if (!isTag) return BittsTooltip;
  return isManagedPartner.value && showTooltip ? BittsTooltip : 'div';
});

const tagColor = computed(() => {
  if (isDeprecatedMop.value) return 'warning';
  return isManagedPartner.value ? 'info' : 'neutral';
});

const iconClass = computed(() => {
  if (isDeprecatedMop.value) return 'text-warning-accent';
  return isManagedPartner.value ? 'text-info-accent' : 'text-neutral-accent';
});

const labelClass = computed(() => {
  if (isDeprecatedMop.value) return 'text-warning-text';
  return isManagedPartner.value ? 'text-info-text' : 'text-neutral-text';
});

const label = computed(() => {
  if (isManagedPartner.value) {
    return hasAddOpenDataPartner.value && !isDeprecatedMop.value
      ? 'Open Data Partner'
      : 'Managed Offline Partner';
  }
  return 'Offline Partner';
});

const tooltipLabel = computed(() => {
  if (hasAddOpenDataPartner.value && !isDeprecatedMop.value)
    return 'Open Data Partners are sourced and maintained by Crossbeam';
  return 'Managed Offline Partner data is sourced and maintained by Crossbeam';
});

const icon = computed(() =>
  isOfflinePartner.value ? 'offline-partner' : 'managed-offline-partner',
);
</script>

<style scoped lang="pcss">
@reference "../../css/theme.css";
:deep(.bitts-tag) {
  @apply flex items-center align-middle cursor-default;
}
</style>
